/**
 * Пункт ченджлога: что решила модель (правка карточки / новая статья / база не меняется), почему
 * и что меняем, сравнение редакций, основания — замечания с контекстом, комментарии редактора
 * (текст и голос), решения. Решение несёт ревизию, которую видит редактор: пункт уже доработан —
 * сервер откажет «обновите». Набранный комментарий уходит вместе с решением, не теряется.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BookOpen, ChevronDown, ChevronRight, MessageSquarePlus } from "lucide-react";
import { useState } from "react";

import { ProposalDiff } from "@/components/qc/ProposalDiff";
import { RemarkCard, when } from "@/components/qc/RemarkCard";
import { useVoice, VoiceControl, voiceBusy } from "@/components/voice/VoiceInput";
import {
  ACTION_LABEL,
  approveItem,
  audioUrl,
  type Cities,
  commentItem,
  ITEM_STATUS_LABEL,
  type QcComment,
  type QcItem,
  rejectItem,
  retryItem,
  reworkItem,
  uploadAudio,
} from "@/lib/qc";

const ACTION_TONE = { edit: "bg-accent/15 text-accent", create: "bg-ok-bg text-ok", none: "bg-bg-subtle text-fg-muted" } as const;

export function CommentLine({ c }: { c: QcComment }) {
  return (
    <div className="rounded-[8px] bg-bg-subtle px-2 py-1 text-[12.5px]">
      <span className="font-medium">{c.author || "руководитель"}</span>{" "}
      <span className="text-fg-muted">
        · {when(c.date)} · к ревизии {c.revision}
        {c.item_id !== null && (c.used ? " · учтён моделью" : " · ещё не учтён")}
      </span>
      {c.text && <div className="whitespace-pre-wrap">{c.text}</div>}
      {c.audio && <audio src={audioUrl("comment", c.id)} controls preload="none" className="mt-0.5 h-7 w-full max-w-[280px]" />}
      {c.audio && (
        <div className="text-fg-muted">
          {c.status === "waiting_voice" ? "расшифровывается…" : c.status === "voice_failed" ? "не расшифровалось — допишите текстом" : `«${c.transcript}»`}
        </div>
      )}
    </div>
  );
}

export function ChangelogItem({
  item,
  picked,
  onPick,
  focused,
  invalidate,
  onCard,
  cities,
}: {
  item: QcItem;
  picked: boolean;
  onPick: (on: boolean) => void;
  focused: boolean;
  invalidate: unknown[];
  onCard: (id: number) => void;
  cities: Cities;
}) {
  const client = useQueryClient();
  const [bases, setBases] = useState(false);
  const [full, setFull] = useState(false);
  const [hist, setHist] = useState(false);
  const [talk, setTalk] = useState(false);
  const [text, setText] = useState("");
  // Голос уходит на сервер сразу после «Стоп» (`@/lib/voice`); с решением — только его токен.
  const [audio, setAudio] = useState<string | null>(null);
  const voice = useVoice({ onDone: async (b, s) => setAudio(await uploadAudio(b, s)) });
  const done = () => {
    setText("");
    setAudio(null);
    voice.reset();
    setTalk(false);
    void client.invalidateQueries({ queryKey: invalidate });
    void client.invalidateQueries({ queryKey: ["qc-summary"] });
  };
  const act = useMutation({
    mutationFn: async (what: "approve" | "reject" | "rework" | "comment" | "retry") => {
      if (what === "retry") return retryItem(item.id);
      const body = { text, audio, revision: item.revision };
      if (what === "approve") return approveItem(item.id, body);
      if (what === "reject") return rejectItem(item.id, body);
      if (what === "rework") return reworkItem(item.id, body);
      return commentItem(item.id, body);
    },
    onSuccess: done,
  });
  const p = item.proposal;
  const open = item.status === "review" || item.status === "failed";
  const hasInput = text.trim().length > 0 || !!audio;
  const pendingNote = item.comments.some((c) => !c.used && c.item_id === item.id);
  // Неотправленная запись (сбой загрузки) держит форму: «Отправить ещё раз» или убрать запись.
  const busy = act.isPending || voiceBusy(voice.v) || !!voice.v.blob;
  const btn = "min-h-9 rounded-full px-3 py-1.5 text-[13px] font-medium disabled:opacity-40";
  const target = item.card;
  const cityOf = (id: number | null) => (id === null ? "все салоны" : (cities[String(id)] ?? `город ${id}`));
  return (
    <article
      id={`qc-item-${item.id}`}
      className={`rounded-[12px] border bg-bg px-3 py-3 ${focused ? "border-accent ring-2 ring-accent/20" : "border-border"}`}
      data-annot="пункт ченджлога"
    >
      <header className="flex flex-wrap items-center gap-2">
        {item.status === "review" && (
          <input type="checkbox" checked={picked} onChange={(e) => onPick(e.target.checked)} title="Выбрать для «Утвердить выбранные»" className="size-5" />
        )}
        <span className={`rounded-full px-2 py-0.5 text-[12px] font-medium ${ACTION_TONE[item.action]}`}>{ACTION_LABEL[item.action]}</span>
        <span className="font-medium">{item.title || target?.title || "—"}</span>
        {target && (
          <button type="button" onClick={() => onCard(target.id)} className="flex items-center gap-1 text-[12px] text-accent hover:underline" title="Карточка и её основания">
            <BookOpen size={12} /> карточка {target.id}, v{target.version} · {cityOf(target.location_id)}
          </button>
        )}
        <span className={`ml-auto text-[12px] ${item.status === "failed" ? "text-err" : item.status === "approved" ? "text-ok" : "text-fg-muted"}`}>
          {ITEM_STATUS_LABEL[item.status]}
          {item.revision > 1 ? ` · доработка ${item.revision - 1}` : ""}
        </span>
      </header>
      {item.error && <p className="mt-1.5 text-[13px] text-err">{item.error}</p>}
      {(item.why || item.change) && (
        <div className="mt-2 grid gap-1 text-[13px]">
          {item.why && (
            <div>
              <span className="text-fg-muted">Почему: </span>
              {item.why}
            </div>
          )}
          {item.change && (
            <div>
              <span className="text-fg-muted">Что меняем: </span>
              {item.change}
            </div>
          )}
          {item.action === "none" && target && item.result_version && item.status !== "approved" && (
            <div className="text-fg-muted">Модель сверялась с «{target.title}», v{item.result_version}</div>
          )}
        </div>
      )}
      {p && (
        <div className="mt-2">
          <div className="mb-1 flex flex-wrap gap-x-3 gap-y-1 text-[12px]">
            <button type="button" onClick={() => setFull(false)} className={full ? "text-fg-muted hover:text-fg" : "font-medium"}>
              Изменения {p.before ? `v${p.base_version} → предложение` : ""}
            </button>
            <button type="button" onClick={() => setFull(true)} className={full ? "font-medium" : "text-fg-muted hover:text-fg"}>
              Полный текст
            </button>
            {target && p.before && target.version !== p.base_version && item.status === "review" && (
              <span className="text-warn">карточку уже правили (сейчас v{target.version}) — утверждение уйдёт на доработку</span>
            )}
          </div>
          {full ? (
            <div className="whitespace-pre-wrap rounded-[8px] border border-border px-2 py-1.5 text-[13px]">
              <b>{p.after.title}</b>
              {"\n"}
              {p.after.body}
            </div>
          ) : (
            <ProposalDiff before={p.before} after={p.after} cityOf={cityOf} />
          )}
        </div>
      )}
      {item.status === "approved" && (
        <p className="mt-2 text-[13px] text-ok">
          {item.action === "none"
            ? target
              ? `Согласовано: правило уже есть — «${target.title}», v${item.result_version ?? target.version}`
              : "Согласовано: база не меняется"
            : target
              ? `Внесено в базу: «${target.title}», v${item.result_version ?? target.version} · поисковая копия ${target.search}`
              : "Внесено в базу"}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-3 text-[12.5px]">
        <button type="button" onClick={() => setBases((v) => !v)} className="flex min-h-8 items-center gap-0.5 text-accent hover:underline">
          {bases ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Основания {item.remarks.length}
        </button>
        {item.history.length > 0 && (
          <button type="button" onClick={() => setHist((v) => !v)} className="flex min-h-8 items-center gap-0.5 text-fg-muted hover:text-fg">
            {hist ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Прежние решения {item.history.length}
          </button>
        )}
        {item.comments.length > 0 && <span className="text-fg-muted">комментариев {item.comments.length}</span>}
      </div>
      {bases && (
        <div className="mt-1.5 flex flex-col gap-1.5 border-l-2 border-border pl-2">
          {item.remarks.map((r) => (
            <RemarkCard key={r.id} r={r} invalidate={invalidate} />
          ))}
        </div>
      )}
      {hist && (
        <ol className="mt-1.5 flex flex-col gap-1 border-l-2 border-border pl-2 text-[12.5px] text-fg-muted">
          {item.history.map((h) => (
            <li key={h.revision}>
              ревизия {h.revision} · {when(h.date)}: {ACTION_LABEL[h.action]} {h.title ? `«${h.title}»` : ""} — {h.why}
              {h.comments?.length ? ` · после неё учтены комментарии №${h.comments.join(", №")}` : ""}
            </li>
          ))}
        </ol>
      )}
      {item.comments.length > 0 && (
        <div className="mt-2 flex flex-col gap-1">
          {item.comments.map((c) => (
            <CommentLine key={c.id} c={c} />
          ))}
        </div>
      )}
      {(open || item.status === "rework" || item.status === "drafting") && (
        <div className="mt-2 flex flex-col gap-1.5">
          {talk && (
            <>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={2}
                autoFocus
                placeholder="Что не так, что учесть — или голосом. Уйдёт вместе с решением."
                className="w-full rounded-[8px] border border-border bg-transparent px-2 py-1.5 text-[13.5px] max-md:text-[16px]"
              />
              <div className="flex flex-wrap items-center gap-1.5">
                <VoiceControl
                  voice={voice}
                  disabled={act.isPending || !!audio}
                  onDrop={audio ? () => setAudio(null) : undefined}
                  sentText="Голос загружен — уйдёт вместе с решением"
                />
                <button type="button" onClick={() => act.mutate("comment")} disabled={busy || !hasInput} className={`${btn} ml-auto hover:bg-bg-hover`}>
                  Только комментарий
                </button>
              </div>
            </>
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            {!talk && (
              <button type="button" onClick={() => setTalk(true)} className={`${btn} flex items-center gap-1 text-fg-muted hover:bg-bg-hover`}>
                <MessageSquarePlus size={15} /> Комментарий / голос
              </button>
            )}
            <div className="ml-auto flex flex-wrap gap-1.5">
              {item.status === "failed" && (
                <button type="button" onClick={() => act.mutate("retry")} disabled={busy} className={`${btn} hover:bg-bg-hover`}>
                  Повторить
                </button>
              )}
              {open && (
                <button
                  type="button"
                  onClick={() => (hasInput || pendingNote ? act.mutate("rework") : setTalk(true))}
                  disabled={busy}
                  title="Модель переделает пункт с учётом комментариев"
                  className={`${btn} bg-warn-bg text-warn`}
                >
                  На доработку
                </button>
              )}
              {open && (item.action !== "none" || item.status === "failed") && (
                <button
                  type="button"
                  onClick={() => window.confirm("Отклонить пункт? База знаний не изменится.") && act.mutate("reject")}
                  disabled={busy}
                  className={`${btn} bg-err-bg text-err`}
                >
                  Отклонить
                </button>
              )}
              {item.status === "review" && (
                <button type="button" onClick={() => act.mutate("approve")} disabled={busy} className={`${btn} bg-accent text-white`}>
                  {item.action === "none" ? "Согласен" : "Утвердить"}
                </button>
              )}
            </div>
          </div>
          {open && talk && !hasInput && !pendingNote && <p className="text-[12px] text-fg-muted">Для доработки напишите или надиктуйте, что поправить.</p>}
          {act.isError && <p className="text-[12.5px] text-err">{act.error.message}</p>}
        </div>
      )}
      {item.status === "rejected" && <p className="mt-2 text-[12.5px] text-fg-muted">Отклонено {item.decided_at ? when(item.decided_at) : ""}</p>}
    </article>
  );
}
